import { defaultConditions, defaultSettings, sampleBonusRules } from '../data/defaults'
import { makeId } from './id'
import { todayISO } from './date'

const DB_NAME = 'dohodpro-indexeddb'
const DB_VERSION = 1
const STORES = ['rates', 'bonusRules', 'taxRules', 'shifts', 'payouts', 'conditions', 'settings']

let dbPromise

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function transactionDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

export function openDatabase() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      for (const storeName of STORES) {
        if (!db.objectStoreNames.contains(storeName)) {
          const keyPath = storeName === 'settings' ? 'key' : storeName === 'conditions' ? 'code' : 'id'
          const store = db.createObjectStore(storeName, { keyPath })
          if (storeName === 'shifts') store.createIndex('date', 'date', { unique: false })
          if (storeName === 'rates') store.createIndex('effectiveFrom', 'effectiveFrom', { unique: false })
          if (storeName === 'bonusRules') store.createIndex('active', 'active', { unique: false })
        }
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  return dbPromise
}

export async function getAll(storeName) {
  const db = await openDatabase()
  return requestToPromise(db.transaction(storeName, 'readonly').objectStore(storeName).getAll())
}

export async function getOne(storeName, key) {
  const db = await openDatabase()
  return requestToPromise(db.transaction(storeName, 'readonly').objectStore(storeName).get(key))
}

export async function putOne(storeName, value) {
  const db = await openDatabase()
  const tx = db.transaction(storeName, 'readwrite')
  tx.objectStore(storeName).put(value)
  await transactionDone(tx)
  return value
}

export async function putMany(storeName, values) {
  const db = await openDatabase()
  const tx = db.transaction(storeName, 'readwrite')
  const store = tx.objectStore(storeName)
  for (const value of values) store.put(value)
  await transactionDone(tx)
}

export async function deleteOne(storeName, key) {
  const db = await openDatabase()
  const tx = db.transaction(storeName, 'readwrite')
  tx.objectStore(storeName).delete(key)
  await transactionDone(tx)
}


export async function deleteMany(storeName, keys) {
  const db = await openDatabase()
  const tx = db.transaction(storeName, 'readwrite')
  const store = tx.objectStore(storeName)
  for (const key of keys) store.delete(key)
  await transactionDone(tx)
}

export async function clearStore(storeName) {
  const db = await openDatabase()
  const tx = db.transaction(storeName, 'readwrite')
  tx.objectStore(storeName).clear()
  await transactionDone(tx)
}

export async function seedIfEmpty() {
  const settings = await getOne('settings', 'main')
  if (!settings) await putOne('settings', defaultSettings)

  const conditions = await getAll('conditions')
  if (!conditions.length) await putMany('conditions', defaultConditions)

  const rates = await getAll('rates')
  if (!rates.length) {
    await putOne('rates', {
      id: makeId(),
      effectiveFrom: todayISO(),
      hourlyRate: 100,
      orderRate: 25,
      comment: 'Начальная ставка. Измените её под свои условия.'
    })
  }

  const bonusRules = await getAll('bonusRules')
  if (!bonusRules.length) {
    await putMany('bonusRules', sampleBonusRules.map((rule) => ({ ...rule, id: makeId() })))
  }
}

export async function exportDatabase() {
  const result = {}
  for (const storeName of STORES) result[storeName] = await getAll(storeName)
  result.exportedAt = new Date().toISOString()
  result.app = 'ДоходПро'
  result.version = DB_VERSION
  return result
}

export async function importDatabase(payload) {
  for (const storeName of STORES) {
    if (Array.isArray(payload[storeName])) {
      await clearStore(storeName)
      await putMany(storeName, payload[storeName])
    }
  }
}
